/**
 * Project data for the Build the Stack portfolio
 * Comprehensive collection of Lucas Stufflebeam's projects across enterprise systems,
 * game development, web applications, and personal projects.
 */

export interface Project {
  id: string
  title: string
  category:
    | 'enterprise'
    | 'mobile-games'
    | 'console-games'
    | 'web-development'
    | 'personal-projects'
  description: string
  achievements: string[]
  technologies: string[]
  metrics?: string
  link?: string
  github?: string
  featured?: boolean
}

export const projects: Project[] = [
  // Enterprise & Current Projects
  {
    id: 'regis-platform',
    title: 'SimGate Enterprise Platform Architecture',
    category: 'enterprise',
    description:
      'Principal architect for platforms supporting 100,000+ concurrent users with zero-downtime deployments.',
    achievements: [
      'Architected scalable platforms handling 100,000+ concurrent users',
      'Led integration of LLM-powered DevOps workflows',
      'Achieved 40% cloud cost reduction',
      'Implemented 4x developer throughput improvement',
      'Designed compliance-ready systems for enterprise clients',
    ],
    technologies: [
      'Kubernetes',
      'Istio',
      'Azure',
      'Docker',
      'GitLab',
      'NodeJS',
      'MongoDB',
      'PostgreSQL',
      'Redis',
    ],
    metrics: '100,000+ CCU, 40% cost reduction',
    featured: true,
  },
  {
    id: 'ai-devops-integration',
    title: 'AI-Powered DevOps Platform',
    category: 'enterprise',
    description:
      'End-to-end AI integration into DevOps workflows, from task design to production deployment, with comprehensive testing and impact analysis.',
    achievements: [
      'Designed AI-driven task planning and estimation system',
      'Implemented automated test case generation and validation',
      'Created intelligent change impact detection and analysis',
      'Achieved 90% reduction in regression issues and testing time',
      'Achieved 400% faster development velocity',
    ],
    technologies: [
      'Python',
      'Anthropic Claude',
      'Google Gemini',
      'Kubernetes',
      'Prometheus',
      'Grafana',
      'Docker',
      'Vitest',
      'MCP',
      'PocketFlow',
    ],
    metrics: '90% fewer regression issues, 400% faster development velocity',
    featured: true,
  },

  // Personal Projects
  {
    id: 'horseshoe-hell',
    title: 'Horseshoe Hell Competition Registration System',
    category: 'personal-projects',
    description:
      'Competition registration system with offline-first mobile app for logging routes in remote areas with unreliable internet connectivity.',
    achievements: [
      'Built offline-first architecture for reliable data capture',
      'Implemented route logging with GPS tracking capabilities',
      'Created seamless sync when connectivity is restored',
      'Designed competition registration and management system',
      'Optimized for extreme outdoor conditions with poor network coverage',
    ],
    technologies: [
      'Progressive Web App',
      'Service Workers',
      'IndexedDB',
      'GPS API',
      'JavaScript',
    ],
    link: 'https://horseshoehellapp.com/',
    metrics: 'Offline-first architecture, GPS route tracking',
    featured: true,
  },

  // Mobile Games (Aeria Games Era)
  {
    id: 'immortalis',
    title: 'Immortalis',
    category: 'mobile-games',
    description:
      'Enhanced and maintained a massive mobile web game serving 10,000+ daily active users with new features and performance optimizations.',
    achievements: [
      'Maintained 10,000+ daily active users',
      'Ported features from Japanese to American/European markets',
      'Rewrote large sections of legacy code for performance',
      'Fixed critical bugs affecting user experience',
      'Implemented new gameplay features and systems',
    ],
    technologies: ['HTML5', 'JavaScript', 'CSS', 'PHP', 'MySQL'],
    metrics: '10,000+ DAU',
  },
  {
    id: 'xmen-battle-atom',
    title: 'X-Men: Battle of the Atom',
    category: 'mobile-games',
    description:
      'Designed and developed a collectible card battle game for mobile with comprehensive backend systems and live event management.',
    achievements: [
      'Built scalable database supporting 10,000+ daily users',
      'Created comprehensive Content Management System',
      'Implemented large-scale Raid and Ranking events',
      'Developed Game Master tools for player management',
      'Added major features: Recruiting, Mini Games, News, Trading',
    ],
    technologies: ['Unity', 'C#', 'PHP', 'MySQL', 'JavaScript'],
    metrics: '10,000+ DAU, Live events system',
  },
  {
    id: 'assassins-creed-memories',
    title: 'Assassins Creed: Memories',
    category: 'mobile-games',
    description:
      'Ported core functionality from X-Men: Battle of the Atom and created comprehensive management systems for this major franchise title.',
    achievements: [
      'Ported large amounts of core game functionality',
      'Created automated event creation system',
      'Developed Game Master tools and player management software',
      'Implemented in-depth Content Management System',
    ],
    technologies: ['Unity', 'C#', 'PHP', 'MySQL'],
  },

  // Console Games (Black Lantern Studios Era)
  {
    id: 'zhu-zhu-pets-2',
    title: 'Zhu Zhu Pets 2',
    category: 'console-games',
    description:
      'Enhanced Nintendo DS game with world map navigation, character progression, and HUD systems.',
    achievements: [
      'Implemented world map navigation system',
      'Created character select and unlock progression',
      'Designed and built in-game HUD interface',
      'Optimized for Nintendo DS hardware constraints',
    ],
    technologies: ['C++', 'Nintendo DS SDK'],
    metrics: 'Nintendo DS platform',
  },
  {
    id: 'zhu-zhu-babies',
    title: 'ZhuZhu Babies',
    category: 'console-games',
    description:
      'Led optimization project reducing game size from 19MB to 7MB while adding features and creating development tools.',
    achievements: [
      'Reduced game size from 19MB to 7MB without feature loss',
      'Compressed save data from 256KB to 16KB',
      'Led team of programmer and artist',
      'Created level design tools in C#',
      'Added new gameplay features with new art implementation',
    ],
    technologies: ['C++', 'C#', 'Nintendo DS SDK'],
    metrics: '63% size reduction, 94% save data compression',
    featured: true,
  },
  {
    id: 'nicktoons-mlb',
    title: 'Nicktoons MLB / MLB 3D',
    category: 'console-games',
    description:
      'Created cross-platform tools and systems enabling single assets to work across both DS and 3DS versions.',
    achievements: [
      'Built cross-platform asset pipeline for DS/3DS',
      'Integrated FMOD audio system for 3DS',
      'Implemented synchronized audio across both platforms',
      'Created tools in C# and C++ for asset management',
    ],
    technologies: ['C++', 'C#', 'FMOD', 'Nintendo DS/3DS SDK'],
  },
  {
    id: 'gogos-crazy-bones',
    title: "GoGo's Crazy Bones",
    category: 'console-games',
    description:
      'Led development of Angry Birds-style physics game for Nintendo DS with optimized Box2D integration.',
    achievements: [
      'Led project with 1 programmer and 2 artists',
      'Optimized Box2D physics for DS hardware',
      'Created Angry Birds-style gameplay mechanics',
      'Managed full development lifecycle',
    ],
    technologies: ['C++', 'Box2D', 'Nintendo DS SDK'],
  },

  // Web Development Projects
  {
    id: 'boulder-valley-enrollment',
    title: 'Boulder Valley School District Enrollment System',
    category: 'web-development',
    description:
      'Developed comprehensive preschool enrollment web application with dynamic menu system supporting 34 enrollment paths.',
    achievements: [
      'Built dynamic menu system for 34 enrollment paths',
      'Created client-facing enrollment interface',
      'Integrated with admin console and database systems',
      'Delivered on aggressive timeline with large team',
      'Ensured proper data storage and retrieval',
    ],
    technologies: ['ASP.NET', 'C#', 'SQL Server 2012'],
    metrics: '34 enrollment path combinations',
  },
  {
    id: 'usda-pathogen-database',
    title: 'USDA Pathogen Database Optimization',
    category: 'web-development',
    description:
      'Dramatically improved performance of existing PHP web crawler and database search systems.',
    achievements: [
      'Improved web crawler speed by 720%',
      'Enhanced database search speed by 430%',
      'Added additional search functionality',
      'Optimized existing PHP codebase',
    ],
    technologies: ['PHP', 'MySQL', 'Web Crawling'],
    metrics: '720% crawler improvement, 430% search improvement',
    featured: true,
  },
  {
    id: 'cybersource-mobile-payments',
    title: 'Mobile Payment System',
    category: 'web-development',
    description:
      'Optimized existing payment system for mobile devices with multi-step workflow and multiple payment methods.',
    achievements: [
      'Created mobile-optimized payment workflow',
      'Integrated multiple payment methods (credit, PayPal, bank)',
      'Implemented validation and processing systems',
      'Built responsive multi-step interface',
    ],
    technologies: ['Java', 'JBoss', 'Telerik', 'CentOS'],
  },
]

export const categories = [
  { id: 'all', label: 'All Projects' },
  { id: 'enterprise', label: 'Enterprise Systems' },
  { id: 'mobile-games', label: 'Mobile Games' },
  { id: 'console-games', label: 'Console Games' },
  { id: 'web-development', label: 'Web Development' },
  { id: 'personal-projects', label: 'Personal Projects' },
] as const

/**
 * Get projects by category
 */
export function getProjectsByCategory(categoryId: string): Project[] {
  if (categoryId === 'all') {
    return projects
  }
  return projects.filter(project => project.category === categoryId)
}

/**
 * Get featured projects
 */
export function getFeaturedProjects(): Project[] {
  return projects.filter(project => project.featured)
}

/**
 * Get project statistics
 */
export function getProjectStats() {
  return {
    totalProjects: 36, //projects.length,
    platforms: 8, //new Set(projects.flatMap(p => p.technologies)).size,
    featuredProjects: projects.filter(p => p.featured).length,
    categories: categories.length - 1, // Exclude "all" category
  }
}
